import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip } from '@material-ui/core';
import { renderField } from '../fieldRenderers';
import { DataElementChips } from './DataElementChips';
import { extractDataElementValues } from './DataElementChips';

/**
 * Render summaryItems.columns.config as a table
 */
const renderConfigTable = (configArray, classes) => {
	if (!Array.isArray(configArray) || configArray.length === 0) {
		return <Typography color="textSecondary">(No config data)</Typography>;
	}

	// Get all unique keys from all config objects
	const allKeys = new Set();
	configArray.forEach((item) => {
		if (typeof item === 'object' && item !== null) {
			Object.keys(item).forEach((key) => allKeys.add(key));
		}
	});

	const columns = Array.from(allKeys);

	if (columns.length === 0) {
		return <Typography color="textSecondary">(Empty config)</Typography>;
	}

	return (
		<TableContainer component={Paper} style={{ marginTop: 8 }}>
			<Table size="small">
				<TableHead>
					<TableRow>
						{columns.map((col) => (
							<TableCell key={col}>{col}</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{configArray.map((item, index) => (
						<TableRow key={index}>
							{columns.map((col) => {
								const value = item[col];
								let cellContent = null;

								if (value === undefined || value === null) {
									cellContent = <Typography color="textSecondary">-</Typography>;
								} else if (col === 'selectedDataElement' && Array.isArray(value)) {
									const values = extractDataElementValues(value);
									if (values.length === 0) {
										cellContent = <Typography color="textSecondary">(Empty)</Typography>;
									} else {
										cellContent = (
											<Box display="flex" flexWrap="wrap" style={{ gap: '4px' }}>
												{values.map((val, idx) => (
													<Chip
														key={idx}
														label={val}
														size="small"
														style={{ fontSize: '0.75rem', marginRight: 4, marginBottom: 4 }}
													/>
												))}
											</Box>
										);
									}
								} else if (typeof value === 'object' && !Array.isArray(value)) {
									cellContent = (
										<Typography style={{ wordBreak: 'break-word', maxWidth: 300, fontSize: '0.875rem' }}>
											{JSON.stringify(value, null, 2)}
										</Typography>
									);
								} else if (Array.isArray(value)) {
									cellContent = (
										<Typography style={{ wordBreak: 'break-word', maxWidth: 300, fontSize: '0.875rem' }}>
											{value.length > 0 ? JSON.stringify(value) : '(Empty array)'}
										</Typography>
									);
								} else {
									cellContent = (
										<Typography style={{ wordBreak: 'break-word', maxWidth: 300 }}>
											{String(value)}
										</Typography>
									);
								}

								return (
									<TableCell key={col} style={{ wordBreak: 'break-word', maxWidth: 300 }}>
										{cellContent}
									</TableCell>
								);
							})}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

/**
 * Render summaryItems object with special handling for columns and selectedDataElement
 */
export const SummaryItemsRenderer = ({ summaryItems, classes, showLabel = true }) => {
	if (!summaryItems || typeof summaryItems !== 'object' || Array.isArray(summaryItems)) {
		return null;
	}

	return (
		<Box mb={2}>
			{/* Main heading: summary items */}
			{showLabel && (
				<Typography variant="h6" gutterBottom style={{ fontWeight: 600, marginBottom: 16, color: '#1976d2' }}>
					summary items
				</Typography>
			)}

			{/* Sub-heading: columns */}
			{summaryItems.columns && typeof summaryItems.columns === 'object' && (
				<Box mb={3}>
					<Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 12, color: '#424242' }}>
						columns
					</Typography>
					<Box mt={1} ml={2}>
						{Object.entries(summaryItems.columns).map(([colKey, colValue]) => {
							// Special handling for config - render as table
							if (colKey === 'config' && Array.isArray(colValue)) {
								return (
									<Box key={colKey} mb={2}>
										<Typography variant="subtitle2" className={classes.fieldLabel} style={{ minWidth: 180, marginBottom: 8 }}>
											config:
										</Typography>
										{renderConfigTable(colValue, classes)}
									</Box>
								);
							}
							<>"selectedDataElement"</>
							// Regular field rendering for other properties - use short label
							return (
								<Box key={colKey} mb={1}>
									{renderField(`${colKey}`, colValue, classes)}
								</Box>
							);
						})}
					</Box>
				</Box>
			)}

			{/* Render selectedDataElement as chips */}
			{summaryItems.selectedDataElement && Array.isArray(summaryItems.selectedDataElement) && (
				<Box mb={2} display="flex" alignItems="center" flexWrap="wrap">
					<Typography variant="subtitle2" className={classes.fieldLabel} style={{ minWidth: 180 }}>
						selectedDataElement:
					</Typography>
					<DataElementChips
						dataElementArray={summaryItems.selectedDataElement}
						classes={classes}
						showLabel={false}
					/>
				</Box>
			)}

			{/* Render other properties in summaryItems */}
			{Object.entries(summaryItems)
				.filter(([subKey]) => subKey !== 'columns' && subKey !== 'selectedDataElement')
				.map(([subKey, subValue]) => (
					<Box key={subKey} mb={2}>
						{renderField(`summaryItems.${subKey}`, subValue, classes)}
					</Box>
				))}
		</Box>
	);
};

